import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CalendarX2, ChevronDown, FileText, Loader2, MoreHorizontal, ShieldCheck, UploadCloud } from 'lucide-react';
import { TopBar } from '../components/layout/TopBar';
type CustomPolicy = {
  name: string;
  description: string;
  kind: 'policy' | 'certificate';
  type: 'PDF' | 'DOCX' | 'Certificate';
  uploaded: string;
  status: 'Processed' | 'Processing' | 'Expired';
  appliesTo: string;
  appliedProducts?: string[];
  rules: string;
  certificate?: {
    number: string;
    body: string;
    issued: string;
    expired: string;
    expiredAgo: string;
    note: string;
  };
};
const customPolicies: CustomPolicy[] = [{
  name: 'GMP Certificate — DairyWorks NZ Limited.pdf',
  description: 'Manufacturing-site GMP certification, issued by AsureQuality.',
  kind: 'certificate',
  type: 'Certificate',
  uploaded: '18 Aug 2026',
  status: 'Expired',
  appliesTo: '2 products',
  appliedProducts: ['Cheese Snack', '+ 1 more'],
  rules: '—',
  certificate: {
    number: 'GMP-NZ-2023-0447',
    body: 'AsureQuality',
    issued: '20/02/2023',
    expired: '20/02/2026',
    expiredAgo: '179 days ago',
    note: "This certificate is expired. Products it's linked to will show it as expired wherever it's applied — replace it here to update every linked product at once."
  }
}, {
  name: 'Marketing_Copy_Guidelines.pdf',
  description: 'Approved language, banned phrases and tone rules for all consumer-facing copy.',
  kind: 'policy',
  type: 'PDF',
  uploaded: '24 Jul 2026',
  status: 'Processed',
  appliesTo: 'All products',
  rules: '18'
}, {
  name: 'Ecomm_Listing_Checklist.pdf',
  description: 'Mandatory fields and imagery requirements for third-party marketplace listings.',
  kind: 'policy',
  type: 'PDF',
  uploaded: '22 Jul 2026',
  status: 'Processed',
  appliesTo: 'All products',
  rules: '12'
}, {
  name: 'Internal_Nutrition_Mandatories.pdf',
  description: 'In-house nutrition thresholds that sit tighter than the regulatory minimum.',
  kind: 'policy',
  type: 'PDF',
  uploaded: '18 Jul 2026',
  status: 'Processed',
  appliesTo: 'Food only',
  rules: '9'
}, {
  name: 'Allergen_Control_Policy.docx',
  description: 'Declaration wording, cross-contact statements and supplier evidence requirements.',
  kind: 'policy',
  type: 'DOCX',
  uploaded: '15 Jul 2026',
  status: 'Processed',
  appliesTo: 'All products',
  rules: '7'
}, {
  name: 'Supplier_Onboarding_Requirements.docx',
  description: 'Documentation a new supplier must provide before first purchase order.',
  kind: 'policy',
  type: 'DOCX',
  uploaded: '26 Jul 2026',
  status: 'Processing',
  appliesTo: 'All products',
  rules: '—'
}];
type TypeFilter = 'All' | 'Policies' | 'Certificates';
export function Policies() {
  const [hasPolicies, setHasPolicies] = useState(true);
  const [searchParams] = useSearchParams();
  const fromReuse = searchParams.get('reuse') !== null || searchParams.get('type') === 'certificates';
  return <>
      <TopBar title="Policies" subtitle="Upload your own compliance policies. Once processed, they apply across this workspace's assessments alongside official regulations." actions={<button type="button" onClick={() => setHasPolicies(true)} className="inline-flex items-center gap-2 rounded-lg bg-evergreen px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-forest-green" data-id="element-2997" data-mp="1t6a7lheuxah6"><UploadCloud className="h-4 w-4" data-id="element-2998" data-mp="23126cok6e3dj" />Upload policy</button>} data-id="element-2996" data-mp="1k4kgkcow0djx" />
      <div className="flex flex-1 flex-col overflow-y-auto bg-[#fafbf8] px-6 py-7 lg:px-10" data-id="element-2999" data-mp="27ueruptk5u22">
        {hasPolicies ? <PoliciesTable defaultFilter={fromReuse ? 'Certificates' : 'All'} data-id="element-3000" data-mp="2buov3upyu6d8" /> : <EmptyPolicies onUpload={() => setHasPolicies(true)} data-id="element-3001" data-mp="2gvmdo8ew12f9" />}
      </div>
    </>;
}
function EmptyPolicies({
  onUpload
}: {
  onUpload: () => void;
}) {
  return <section className="m-auto max-w-md text-center" aria-labelledby="empty-policies-heading" data-id="element-3002" data-mp="286xg7tz0cqen">
      <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl border-2 border-[#bdcabd] bg-white text-forest-green" data-id="element-3003" data-mp="237ubkamxwm77"><ShieldCheck className="h-8 w-8" strokeWidth={1.5} data-id="element-3004" data-mp="2hx0d45992oi9" /></span>
      <h2 id="empty-policies-heading" className="mt-6 font-[Figtree] text-2xl font-semibold text-evergreen" data-id="element-3005" data-mp="2cxnll77fot9c">No custom policies yet</h2>
      <p className="mt-3 text-sm leading-relaxed text-[#687a6d]" data-id="element-3006" data-mp="1p94gf0jokwlh">Upload a PDF or DOCX policy document. After it&apos;s processed, your assessments will check products against it.</p>
      <button type="button" onClick={onUpload} className="mt-7 inline-flex items-center gap-2 rounded-xl bg-evergreen px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-forest-green" data-id="element-3007" data-mp="1z6scbtt0wa58"><UploadCloud className="h-4 w-4" data-id="element-3008" data-mp="3akwehn9rl4ld" />Upload policy</button>
    </section>;
}
const filters: TypeFilter[] = ['All', 'Policies', 'Certificates'];
function PoliciesTable({
  defaultFilter
}: {
  defaultFilter: TypeFilter;
}) {
  const [filter, setFilter] = useState<TypeFilter>(defaultFilter);
  const [openRow, setOpenRow] = useState<string | null>(null);
  const [openProducts, setOpenProducts] = useState<string | null>(null);
  const visible = customPolicies.filter(policy => {
    if (filter === 'Policies') return policy.kind === 'policy';
    if (filter === 'Certificates') return policy.kind === 'certificate';
    return true;
  });
  const activeRules = visible.filter(policy => policy.status === 'Processed').reduce((total, policy) => total + Number(policy.rules), 0);
  return <div className="mx-auto w-full max-w-7xl space-y-4" data-id="element-3009" data-mp="3n4jmy91w92wb">
      <p className="text-sm text-[#718174]" data-id="element-3010" data-mp="3n1xhceykcxgq">{visible.length} documents · {activeRules} rules active</p>

      <div className="flex flex-wrap gap-2" role="group" aria-label="Filter by type" data-id="element-3011" data-mp="3r3inlhgledhy">
        {filters.map(option => {
        const selected = filter === option;
        return <button key={option} type="button" aria-pressed={selected} onClick={() => setFilter(option)} className={`rounded-full border px-3.5 py-1.5 text-sm font-medium transition ${selected ? 'border-forest-green bg-forest-green text-white' : 'border-[#d6dfd3] bg-white text-[#5f7266] hover:bg-[#f4f7f2]'}`} data-id="element-3012" data-mp="nhtj40adw08u">
              {option}
            </button>;
      })}
      </div>

      <section className="overflow-hidden rounded-2xl border border-[#dce3da] bg-white shadow-sm" aria-label="Custom policies" data-id="element-3013" data-mp="efr3abc59pjv">
        <div className="overflow-x-auto" data-id="element-3014" data-mp="5l2ysnee8bw4">
          <table className="w-full min-w-[900px] text-left text-sm" data-id="element-3015" data-mp="5m4c6yivtah0">
            <thead className="border-b border-[#edf0eb] bg-[#fbfdf9] font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-[#718174]" data-id="element-3016" data-mp="mrns9cd3vcb">
              <tr data-id="element-3017" data-mp="3rrrka3g3rkbh">
                <th className="px-6 py-3" data-id="element-3018" data-mp="3mtg6xiqlmroi">Policy</th>
                <th className="px-5 py-3" data-id="element-3019" data-mp="3mur8rd0hhpcz">Type</th>
                <th className="px-5 py-3" data-id="element-3020" data-mp="3cpjakaif470u">Uploaded</th>
                <th className="px-5 py-3" data-id="element-3021" data-mp="3cozrgqh17cl8">Status</th>
                <th className="px-5 py-3" data-id="element-3022" data-mp="3hocepcbteloc">Applies to</th>
                <th className="px-5 py-3 text-right" data-id="element-3023" data-mp="3hmrkjntex4r0">Rules</th>
                <th className="px-5 py-3" aria-label="Actions" data-id="element-3024" data-mp="3mnfheq4boft5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-[#edf0eb]" data-id="element-3025" data-mp="3rlqtz8m054f2">
              {visible.map(policy => {
              const expanded = openRow === policy.name;
              return <React.Fragment key={policy.name} data-id="element-3026" data-mp="2yxl5qbvqoxah">
                    <tr className={`transition ${policy.certificate ? 'cursor-pointer' : ''} hover:bg-[#f8faf7]`} onClick={policy.certificate ? () => setOpenRow(expanded ? null : policy.name) : undefined} data-id="element-3027" data-mp="2yw0eav4q1ugu">
                      <td className="px-6 py-4" data-id="element-3028" data-mp="2b7qxy8qa1yx3">
                        <div className="flex items-start gap-3" data-id="element-3029" data-mp="2g7n0s4n8nkb2">
                          <FileText className="mt-0.5 h-4 w-4 shrink-0 text-[#819083]" data-id="element-3030" data-mp="2g5sh0tnpdy8k" />
                          <div className="min-w-0" data-id="element-3031" data-mp="3uelkabkkysz5">
                            <p className="font-medium text-evergreen" data-id="element-3032" data-mp="3qcrqmn8syp8h">{policy.name}</p>
                            <p className="mt-0.5 text-sm text-[#8b968d]" data-id="element-3033" data-mp="86vh487cnzt3">{policy.description}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-4 text-[#617366]" data-id="element-3034" data-mp="3a51flzgj687">{policy.type}</td>
                      <td className="px-5 py-4 text-[#617366]" data-id="element-3035" data-mp="h5qnbkae99we">{policy.uploaded}</td>
                      <td className="px-5 py-4" data-id="element-3036" data-mp="d56n27uixq61">
                        {policy.status === 'Processed' && <span className="inline-flex rounded-full bg-forest-green/10 px-2.5 py-1 text-xs font-semibold text-forest-green" data-id="element-3037" data-mp="qwl9yprjybid">Processed</span>}
                        {policy.status === 'Processing' && <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-800" data-id="element-3038" data-mp="vwpssmfxnibr">
                            <Loader2 className="h-3.5 w-3.5 animate-spin" data-id="element-3039" data-mp="1fj12eoupuvmq" />
                            Processing
                          </span>}
                        {policy.status === 'Expired' && <span className="inline-flex items-center gap-1.5 rounded-full bg-red-100 px-2.5 py-1 text-xs font-semibold text-red-800" data-id="element-3040" data-mp="1aj6achz7g003">
                            <CalendarX2 className="h-3.5 w-3.5" data-id="element-3041" data-mp="vtgx3odu85jg" />
                            Expired
                          </span>}
                      </td>
                      <td className="px-5 py-4 text-[#617366]" data-id="element-3042" data-mp="mun407zptxan">
                        {policy.appliedProducts ? <div data-id="element-3043" data-mp="rt6qqam4vd42">
                            <button type="button" aria-expanded={openProducts === policy.name} onClick={event => {
                        event.stopPropagation();
                        setOpenProducts(openProducts === policy.name ? null : policy.name);
                      }} className="inline-flex items-center gap-1.5 rounded-full border border-[#d6dfd3] bg-white px-2.5 py-1 text-xs font-semibold text-[#5f7266] transition hover:bg-[#f4f7f2]" data-id="element-3044" data-mp="rqarmiyjuie1">
                              {policy.appliesTo}
                              <ChevronDown className={`h-3.5 w-3.5 transition ${openProducts === policy.name ? 'rotate-180' : ''}`} data-id="element-3045" data-mp="2zo6j6bqafenu" />
                            </button>
                            {openProducts === policy.name && <ul className="mt-2 space-y-1 text-xs text-[#748477]" data-id="element-3046" data-mp="2uwoh1kcj381a">
                                {policy.appliedProducts.map(product => <li key={product} data-id="element-3047" data-mp="3ikxyka2pl9mm">{product}</li>)}
                              </ul>}
                          </div> : policy.appliesTo}
                      </td>
                      <td className="px-5 py-4 text-right font-mono text-[#617366]" data-id="element-3048" data-mp="16x2w0kq0i5kd">{policy.rules}</td>
                      <td className="px-5 py-4 text-right" data-id="element-3049" data-mp="1lpmie9oxzyf4">
                        <button type="button" aria-label={`Actions for ${policy.name}`} onClick={event => event.stopPropagation()} className="rounded-lg p-2 text-[#718174] hover:bg-[#edf2ec]" data-id="element-3050" data-mp="1gq1ju0dops2x">
                          <MoreHorizontal className="h-4 w-4" data-id="element-3051" data-mp="11xpx31lv981f" />
                        </button>
                      </td>
                    </tr>
                    {expanded && policy.certificate && <tr className="bg-[#fbfdf9]" data-id="element-3052" data-mp="2dh2d6r0nkrgf">
                        <td colSpan={7} className="px-6 py-5" data-id="element-3053" data-mp="29cckfb1ajszq">
                          <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-id="element-3054" data-mp="162gv4be17y0h">
                            <DetailField label="Certificate number" value={policy.certificate.number} mono data-id="element-3055" data-mp="164ungchbuw4c" />
                            <DetailField label="Certifying body" value={policy.certificate.body} data-id="element-3056" data-mp="1g0o9qnkum3zb" />
                            <DetailField label="Issued" value={policy.certificate.issued} mono data-id="element-3057" data-mp="1k29h5tem5q24" />
                            <DetailField label="Expired" value={`${policy.certificate.expired} · ${policy.certificate.expiredAgo}`} mono tone="red" data-id="element-3058" data-mp="nbz5wgserkjv" />
                          </dl>
                          <p className="mt-4 max-w-3xl rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm leading-relaxed text-red-900" data-id="element-3059" data-mp="113nggfn0e0yq">
                            {policy.certificate.note}
                          </p>
                        </td>
                      </tr>}
                  </React.Fragment>;
            })}
            </tbody>
          </table>
        </div>
      </section>

      <button type="button" className="flex w-full flex-col items-center gap-2 rounded-2xl border-2 border-dashed border-[#c9d4c7] bg-white px-6 py-8 text-center transition hover:border-forest-green/50 hover:bg-[#fbfdf9]" data-id="element-3060" data-mp="3saefia7whgr8">
        <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#eff3ed] text-forest-green" data-id="element-3061" data-mp="3e9tv59mc7cvm"><UploadCloud className="h-5 w-5" data-id="element-3062" data-mp="39ivaxyy89un3" /></span>
        <span className="font-[Figtree] text-base font-semibold text-evergreen" data-id="element-3063" data-mp="39i2blj1lm7ez">Drop a policy here or click to browse</span>
        <span className="text-sm text-[#8b968d]" data-id="element-3064" data-mp="34hxrzk5e8wkj">Accepted: PDF, DOCX · Max size: 10 MB per file</span>
      </button>
    </div>;
}
function DetailField({
  label,
  value,
  mono,
  tone
}: {
  label: string;
  value: string;
  mono?: boolean;
  tone?: 'red';
}) {
  return <div data-id="element-3065" data-mp="2qlu6qgchzyk7">
      <dt className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-[#7a897d]" data-id="element-3066" data-mp="2qlawzmx04gh9">{label}</dt>
      <dd className={`mt-1 text-sm ${mono ? 'font-mono' : ''} ${tone === 'red' ? 'font-semibold text-red-700' : 'text-[#4f6256]'}`} data-id="element-3067" data-mp="ujh8k4vij3xa">{value}</dd>
    </div>;
}